import type { DayRange } from '../../lib/format/dates';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  acknowledgeAlert,
  adjustListerBalance,
  createChefPayout,
  drawDownBuffer,
  getAlertRegistry,
  downloadAuditExport,
  exportBufferLedger,
  exportFilingExtract,
  exportRevenue,
  getBufferLedger,
  getFilingCalendar,
  getFilingExtract,
  getHomeRule,
  getListerBalance,
  getLocaleHealth,
  getNexus,
  getPayoutsAging,
  getRevenue,
  listAlerts,
  markFiled,
  listChefPayouts,
  patchChefPayout,
  runReconciliation,
  type MarkFiledInput,
  type HealthView,
  type PayoutStatus,
  type Rail,
  type ReportWindow,
} from './api';

export const moneyKeys = {
  all: ['money'] as const,
  health: (p: object) => [...moneyKeys.all, 'locale-health', p] as const,
  nexus: () => [...moneyKeys.all, 'nexus'] as const,
  homeRule: () => [...moneyKeys.all, 'home-rule'] as const,
  aging: (ids: string[]) => [...moneyKeys.all, 'aging', ids] as const,
  payouts: (p: object) => [...moneyKeys.all, 'chef-payouts', p] as const,
  balance: (id: string, p: object) => [...moneyKeys.all, 'balance', id, p] as const,
  filing: () => [...moneyKeys.all, 'filing-calendar'] as const,
  alerts: (p: object) => [...moneyKeys.all, 'alerts', p] as const,
  registry: () => [...moneyKeys.all, 'alert-registry'] as const,
  extract: (p: object) => [...moneyKeys.all, 'filing-extract', p] as const,
  ledger: (p: object) => [...moneyKeys.all, 'buffer-ledger', p] as const,
  revenue: (p: object) => [...moneyKeys.all, 'revenue', p] as const,
};

/** Every money read shows its error inline, so all are silent. */
const base = { meta: { silent: true } } as const;

export const useLocaleHealth = (p: { days: DayRange; localeIds: string[]; view?: HealthView }) =>
  useQuery({
    ...base,
    queryKey: moneyKeys.health(p),
    queryFn: ({ signal }) => getLocaleHealth(p, signal),
    placeholderData: keepPreviousData,
  });

export const useNexus = (days: DayRange) =>
  useQuery({
    ...base,
    queryKey: [...moneyKeys.nexus(), days] as const,
    queryFn: ({ signal }) => getNexus(days, signal),
    placeholderData: keepPreviousData,
  });

export const useHomeRule = () =>
  useQuery({ ...base, queryKey: moneyKeys.homeRule(), queryFn: ({ signal }) => getHomeRule(signal) });

export const usePayoutsAging = (localeIds: string[]) =>
  useQuery({
    ...base,
    queryKey: moneyKeys.aging(localeIds),
    queryFn: ({ signal }) => getPayoutsAging(localeIds, signal),
    placeholderData: keepPreviousData,
  });

export const useChefPayouts = (p: {
  chefProfileId?: string;
  status?: PayoutStatus;
  pageSize: number;
  offset: number;
}) =>
  useQuery({
    ...base,
    queryKey: moneyKeys.payouts(p),
    queryFn: ({ signal }) => listChefPayouts(p, signal),
    placeholderData: keepPreviousData,
  });

export const useListerBalance = (profileId: string, p: { pageSize: number; offset: number }) =>
  useQuery({
    ...base,
    queryKey: moneyKeys.balance(profileId, p),
    queryFn: ({ signal }) => getListerBalance(profileId, p, signal),
  });

export const useFilingCalendar = () =>
  useQuery({ ...base, queryKey: moneyKeys.filing(), queryFn: ({ signal }) => getFilingCalendar(signal) });

export const useAlerts = (p: { status?: 'open' | 'acknowledged'; code?: number; pageSize: number; offset: number }) =>
  useQuery({
    ...base,
    queryKey: moneyKeys.alerts(p),
    queryFn: ({ signal }) => listAlerts(p, signal),
    placeholderData: keepPreviousData,
  });

export const useAlertRegistry = () =>
  useQuery({
    ...base,
    queryKey: moneyKeys.registry(),
    queryFn: ({ signal }) => getAlertRegistry(signal),
    staleTime: 5 * 60_000,
  });

export const useFilingExtract = (p: ReportWindow & { state?: string }, enabled: boolean) =>
  useQuery({ ...base, queryKey: moneyKeys.extract(p), queryFn: ({ signal }) => getFilingExtract(p, signal), enabled });

export const useBufferLedger = (p: { locale?: string; from?: string; to?: string }, enabled: boolean) =>
  useQuery({ ...base, queryKey: moneyKeys.ledger(p), queryFn: ({ signal }) => getBufferLedger(p, signal), enabled });

export const useRevenue = (p: ReportWindow & { locale?: string; outliers?: boolean }, enabled: boolean) =>
  useQuery({ ...base, queryKey: moneyKeys.revenue(p), queryFn: ({ signal }) => getRevenue(p, signal), enabled });

/** Reconciliation can fire alert #8, so it is a mutation run on an explicit "Run", never a query. */
export function useRunReconciliation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (settlementDate: string) => runReconciliation(settlementDate),
    onSuccess: () => qc.invalidateQueries({ queryKey: moneyKeys.alerts({}).slice(0, 2) }),
    meta: { silent: true },
  });
}

/** Writes refresh every money read; refusals show in the form (silent). */
function useMoneyMutation<A, R>(fn: (args: A) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: moneyKeys.all }),
    meta: { silent: true },
  });
}

export const useAcknowledgeAlert = () =>
  useMoneyMutation(({ id, note }: { id: string; note?: string }) => acknowledgeAlert(id, note));
export const useCreateChefPayout = () =>
  useMoneyMutation((body: { chefProfileId: string; amountCents: number; rail: Rail }) => createChefPayout(body));
export const usePatchChefPayout = () =>
  useMoneyMutation(
    ({ id, ...body }: { id: string; status?: PayoutStatus; externalPayoutId?: string; failureReason?: string }) =>
      patchChefPayout(id, body),
  );
export const useAdjustBalance = () =>
  useMoneyMutation((body: { profileId: string; amountCents: number; note: string; direction: 'credit' | 'debit' }) =>
    adjustListerBalance(body),
  );
export const useMarkFiled = () =>
  useMoneyMutation(({ registrationId, ...body }: MarkFiledInput & { registrationId: string }) =>
    markFiled(registrationId, body),
  );
export const useDrawDownBuffer = () =>
  useMoneyMutation((body: { localeId: string; cityId?: string; amountCents: number; reason: string }) =>
    drawDownBuffer(body),
  );

/** CSV downloads. Silent: the screen shows a failed export inline. */
const useExport = <A>(fn: (args: A) => Promise<void>) => useMutation({ mutationFn: fn, meta: { silent: true } });
export const useExportFilingExtract = () => useExport((p: ReportWindow & { state?: string }) => exportFilingExtract(p));
export const useExportBufferLedger = () =>
  useExport((p: { locale?: string; from?: string; to?: string }) => exportBufferLedger(p));
export const useExportRevenue = () =>
  useExport((p: ReportWindow & { locale?: string; outliers?: boolean }) => exportRevenue(p));
export const useAuditExport = () =>
  useMutation({ mutationFn: (p: { from: string; to: string }) => downloadAuditExport(p), meta: { silent: true } });
